import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Company, CompanyCard } from "@/components/CompanyCard";
import { EntityGrid } from "@/components/common/EntityGrid";
import { useTranslation } from "react-i18next";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useMultiSelect } from "@/hooks/useMultiSelect";
import { DuplicateManager, useDuplicateManager } from "@/components/common";
import { EntityMergeService } from "@/services/entityMergeService";
import { companyMergeConfig } from "@/config/companyMergeConfig";
import { toast } from "@/hooks/use-toast";

interface CompaniesGridProps {
  companies: Company[];
  filteredCompanies: Company[];
  isLoading: boolean;
  onCompaniesChange?: () => void;
  favorites?: Set<string>;
}

export const CompaniesGrid = ({ 
  companies, 
  filteredCompanies, 
  isLoading,
  onDeleteCompanies,
  onCompaniesChange,
  favorites = new Set()
}: CompaniesGridProps & { onDeleteCompanies?: (ids: string[]) => Promise<void> }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const {
    isMultiSelectMode,
    toggleMultiSelect,
    toggleItemSelection,
    handleDeleteSelected,
    isSelected,
    selectedCount,
    selectAll
  } = useMultiSelect(filteredCompanies, onDeleteCompanies || (() => Promise.resolve()));

  const {
    openMergeModal,
    getDuplicateManagerProps
  } = useDuplicateManager(filteredCompanies, isSelected, "companies", companyMergeConfig);

  // Handle company merge
  const handleMerge = async (merged: Company, sources: Company[]) => {
    try {
      const result = await EntityMergeService.mergeCompanies(merged, sources);
      
      if (result.success) {
        toast({
          title: "Companies merged successfully",
          description: `${sources.length} companies have been merged into one.`,
        });
        // Refresh companies list
        if (onCompaniesChange) {
          onCompaniesChange();
        }
        if (result.redirectPath)
          navigate(result.redirectPath);
        return result;
      } else {
        toast({
          title: "Failed to merge companies",
          description: result.error || "An error occurred during merge.",
          variant: "destructive",
        });
        // Rethrow error to prevent modal from closing
        throw new Error(result.error || "An error occurred during merge.");
      }
    } catch (error) {
      console.error("Error merging companies:", error);
      toast({
        title: "Failed to merge companies",
        description: "An unexpected error occurred during merge.",
        variant: "destructive",
      });
      // Rethrow error to prevent modal from closing
      throw error;
    }
  };

  return (
    <>
      <EntityGrid
        data={filteredCompanies}
        isLoading={isLoading}
        emptyMessage={t("companies.empty")}
        renderItem={(company) => (
          <CompanyCard key={company.id} company={company} isSelected={isSelected(company.id)} isFavorite={favorites.has(company.id)} />
        )}
        isMultiSelectMode={isMultiSelectMode}
        onToggleMultiSelect={toggleMultiSelect}
        onToggleItemSelection={toggleItemSelection}
        isItemSelected={isSelected}
        onDeleteSelected={() => setShowDeleteDialog(true)}
        onMergeSelected={openMergeModal}
        selectedCount={selectedCount}
        selectAll={selectAll}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.deleteConfirmation", { count: selectedCount })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.deleteWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleDeleteSelected();
                setShowDeleteDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DuplicateManager
        {...getDuplicateManagerProps(handleMerge)}
      />
    </>
  );
};
